import '@/features/cards/tokens';

import { buildBunnyOverlay } from '@/components/video/bunny-hover-preview';
import { ShortCard, type ShortCardData } from '@/features/cards';
import type { ContentFlagKind } from '@/features/cards/primitives/ContentFlagChip';
import { CARD_IMAGE_SIZES } from '@/shared/constants/card-image-sizes';
import { resolveVideoCountryIso } from '@/shared/utils/country-iso';
import { getChannelHandle, getCreatorRoute } from '@/shared/utils/routes';
import type { Video } from '@/types';
import type { ReactElement } from 'react';

interface ShortCardContainerProps {
  video: Video;
  eager?: boolean;
  sizes?: string;
  /**
   * Force the card into the locked state (LockCorner rendered, Save
   * hidden). Use on freemium gates.
   */
  locked?: boolean;
  /**
   * Override the adapter's computed href (default `/shorts/<uuid>`).
   * Used for freemium gates to route to `/choose-plan` instead.
   */
  href?: string;
  /**
   * Drop the creator row (avatar + @handle) from the card footer. The
   * country flag stays (via `LocatorCountry`). Use on the creator's own
   * channel page where every short belongs to the same creator and the
   * row would be redundant.
   */
  hideCreator?: boolean;
  /**
   * Drop the title text under the thumb. When combined with `hideCreator`
   * and no country in the data, the card collapses to a thumb-only tile
   * (badges + lock + corner flag from MetaStrip). Use on dense channel
   * shorts grids where titles add noise.
   */
  hideTitle?: boolean;
}

function adaptShort(video: Video): ShortCardData {
  const thumbnail = video.thumbnail_webp ?? video.thumbnail ?? video.card_thumbnail ?? '';
  const title = video.title ?? '';
  const country = resolveVideoCountryIso(video);
  const handler = getChannelHandle(video.channel);
  const handle =
    handler !== undefined && handler.length > 0
      ? handler.startsWith('@')
        ? handler
        : `@${handler}`
      : undefined;
  const creatorName = video.channel?.name;
  const creatorAvatarSrc = video.channel?.dp;
  const creatorHref =
    typeof handler === 'string' && handler.length > 0 ? getCreatorRoute(handler) : undefined;

  const flags: ContentFlagKind[] = [];
  if (video.is_adult_content === true) flags.push('sensitive');
  if (video.is_substance_content === true) flags.push('substance');
  if (video.is_exclusive_content === true) flags.push('exclusive');

  return {
    href: `/shorts/${video.uuid ?? video.id}`,
    thumbnailSrc: thumbnail,
    thumbnailAlt: title || 'Short video',
    title,
    ...(video.duration !== undefined && video.duration > 0 ? { duration: video.duration } : {}),
    ...(country !== undefined ? { country } : {}),
    ...(handle !== undefined ? { handle } : {}),
    ...(creatorName !== undefined && creatorName.length > 0 ? { creatorName } : {}),
    ...(creatorAvatarSrc !== undefined && creatorAvatarSrc.length > 0 ? { creatorAvatarSrc } : {}),
    ...(creatorHref !== undefined ? { creatorHref } : {}),
    ...(flags.length > 0 ? { flags } : {}),
    ...(video.is_free === false ? { locked: true } : {}),
  };
}

export function ShortCardContainer({
  video,
  eager,
  sizes,
  locked,
  href,
  hideCreator,
  hideTitle,
}: ShortCardContainerProps): ReactElement {
  const base = adaptShort(video);
  const withOverrides = {
    ...base,
    ...(href !== undefined ? { href } : {}),
    ...(locked === true ? { locked: true } : {}),
  };
  let data: ShortCardData = withOverrides;
  if (hideCreator === true) {
    const { handle: _h, creatorName: _n, creatorAvatarSrc: _a, creatorHref: _hr, ...rest } = data;
    data = rest;
  }
  if (hideTitle === true) {
    const { title: _t, ...rest } = data;
    data = rest;
  }
  // Bunny webp is a static tease, not playback — allowed on locked cards.
  const bunnyOverlay = buildBunnyOverlay(true, video.bunny_video_id);
  return (
    <ShortCard
      video={data}
      {...(eager !== undefined ? { eager } : {})}
      sizes={sizes ?? CARD_IMAGE_SIZES.portrait}
      {...(bunnyOverlay !== null ? { previewOverlay: bunnyOverlay } : {})}
    />
  );
}
